import { useEffect, useMemo, useState } from "react";
import { ClipboardList, Users as UsersIcon, UserCheck, Loader2, FileSearch } from "lucide-react";
import { api } from "../utils/auth";
import { useAuth } from "../context/AuthContext";
import { hasRole } from "../utils/auth";
import {
  loadPrograms,
} from "../utils/programStorage";
import { flattenSubs } from "../utils/programStorage";
import {
  loadAssignments,
  saveAssignmentAPI,
  setAssignment,
  subsAssignedToUser,
  workloadByUser,
} from "../utils/assignmentStorage";
import AssignmentTable from "../components/Penugasan/AssignmentTable";
import AssignmentModal from "../components/Penugasan/AssignmentModal";
import TimSaya from "../components/Penugasan/TimSaya";
import BebanKerjaCards from "../components/Penugasan/BebanKerjaCards";
import TelaahStaffModal from "../components/Penugasan/TelaahStaffModal";
import TelaahanHistory from "../components/Penugasan/TelaahanHistory";
import { Toaster, toast } from "../components/ui/sonner";

const TABS = [
  { id: "assign", label: "Penugasan Sub-Kegiatan", icon: ClipboardList },
  { id: "mine", label: "Tim Saya", icon: UserCheck },
  { id: "workload", label: "Beban Kerja", icon: UsersIcon },
  { id: "telaahan", label: "Telaahan Staff", icon: FileSearch },
];

export default function Penugasan({ defaultTab }) {
  const { user } = useAuth();
  const canEdit = hasRole(user, "IRBAN", "DALNIS");

  const [programs, setPrograms] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [loadingData, setLoadingData] = useState(true);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [tab, setTab] = useState(defaultTab || "assign");
  const [modalOpen, setModalOpen] = useState(false);
  const [activeSub, setActiveSub] = useState(null);
  const [activeTelaahSub, setActiveTelaahSub] = useState(null);

  useEffect(() => {
    let mounted = true;

    Promise.all([loadPrograms(), loadAssignments()])
      .then(([prog, assign]) => {
        if (mounted) {
          setPrograms(prog);
          setAssignments(assign);
          setLoadingData(false);
        }
      })
      .catch(() => {
        if (mounted) setLoadingData(false);
      });

    api
      .get("/users")
      .then((res) => {
        if (mounted) {
          const data = Array.isArray(res.data) ? res.data : [];
          setUsers(data);
        }
      })
      .catch(() => {
        if (mounted) toast.error("Gagal memuat daftar pengguna");
      })
      .finally(() => mounted && setLoadingUsers(false));

    return () => {
      mounted = false;
    };
  }, []);

  const flatSubs = useMemo(() => {
    if (!Array.isArray(programs)) return [];
    return flattenSubs(programs);
  }, [programs]);
  const mySubs = useMemo(
    () => (user ? subsAssignedToUser(assignments, user.id, flatSubs) : []),
    [assignments, flatSubs, user],
  );
  const workload = useMemo(
    () => workloadByUser(assignments, users, flatSubs),
    [assignments, users, flatSubs],
  );

  const openAssign = (sub) => {
    setActiveSub(sub);
    setModalOpen(true);
  };

  const handleSave = async (subId, patch) => {
    if (!canEdit) return;
    const next = setAssignment(assignments, subId, patch);
    setAssignments(next);
    setModalOpen(false);
    try {
      await saveAssignmentAPI(subId, patch);
      toast.success("Penugasan berhasil disimpan");
    } catch (err) {
      toast.error("Gagal menyimpan penugasan: " + (err?.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="space-y-6" data-testid="penugasan-page">
      <Toaster position="top-right" richColors />

      <div className="flex flex-col gap-3 border-b border-gray-200 pb-5 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Koordinasi
          </div>
          <h1 className="mt-1 font-display text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
            Penugasan Tim
          </h1>
          <p className="mt-1 text-[13px] text-slate-500">
            Tentukan Dalnis, Ketua Tim, dan Anggota Tim untuk setiap sub-kegiatan; pantau beban
            kerja personel unit Irbansus.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            Akses Anda
          </div>
          <span
            data-testid="access-label"
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
              canEdit
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-gray-200 bg-gray-50 text-gray-600"
            }`}
          >
            {canEdit ? "Edit" : "Hanya Baca"}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div
        data-testid="penugasan-tabs"
        className="flex items-center gap-1 overflow-x-auto border-b border-gray-200"
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              type="button"
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              aria-selected={active}
              className={`inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-[12.5px] font-semibold transition-colors ${
                active
                  ? "border-[#1e3a5f] text-[#1e3a5f]"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon className="h-4 w-4" strokeWidth={2} />
              {t.label}
            </button>
          );
        })}
      </div>

      {loadingUsers || loadingData ? (
        <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-white py-14">
          <Loader2 className="h-5 w-5 animate-spin text-[#1e3a5f]" />
        </div>
      ) : tab === "assign" ? (
        <AssignmentTable
          programs={programs}
          assignments={assignments}
          users={users}
          canEdit={canEdit}
          onAssign={openAssign}
          onOpenTelaah={setActiveTelaahSub}
          user={user}
        />
      ) : tab === "mine" ? (
        <TimSaya subs={mySubs} user={user} onOpenTelaah={setActiveTelaahSub} />
      ) : tab === "workload" ? (
        <BebanKerjaCards rows={workload} />
      ) : (
        <TelaahanHistory user={user} onOpenTelaah={setActiveTelaahSub} />
      )}

      <TelaahStaffModal
        open={!!activeTelaahSub}
        onOpenChange={(v) => !v && setActiveTelaahSub(null)}
        sub={activeTelaahSub}
        user={user}
      />

      <AssignmentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        sub={activeSub}
        assignment={activeSub ? assignments[activeSub.id] : null}
        users={users}
        canEdit={canEdit}
        onSave={handleSave}
      />
    </div>
  );
}
